import { AxiosError } from 'axios';
import i18n from '@/i18n';
import { del, generateQueryString, get, post } from '@/utils';
import {
	AGREEMENT_TYPE_BY_ID,
	AGREEMENT_TYPE_CONFIG,
	AgrementsStatus,
	AgrementsType,
	IAgreementStatusItem,
	IAgreementTypeItem,
	IAgrements,
	ICreateAgrements
} from './types';

// Fallback DEFENSIV pe nume — folosit doar când payload-ul nu conține niciun id (BE ar
// întoarce doar numele localizat). Rezolvarea normală se face pe id (AGREEMENT_TYPE_BY_ID).
const AGREEMENT_TYPE_NAME_MAP: Record<string, AgrementsType> = {
	GDPR: 'GDPR',
	'Politica de confidențialitate': 'Privacy Policy',
	'Privacy Policy': 'Privacy Policy',
	'Termeni și condiții': 'Terms and conditions',
	'Terms and conditions': 'Terms and conditions'
};

const AGREEMENT_STATUS_NAME_MAP: Record<string, AgrementsStatus> = {
	draft: 'draft',
	Draft: 'draft',
	published: 'published',
	Published: 'published',
	Publicat: 'published',
	unpublished: 'unpublished',
	Unpublished: 'unpublished',
	Nepublicat: 'unpublished',
	archived: 'archived',
	Archived: 'archived',
	Arhivat: 'archived'
};

let agreementTypesCache: IAgreementTypeItem[] | null = null;
let agreementStatusesCache: IAgreementStatusItem[] | null = null;

const appendPaginationParams = (query: string, page: number, items: number): string => {
	const paginationQuery = generateQueryString({
		PageNumber: page + 1,
		PageSize: items
	});

	if (!query) {
		return paginationQuery;
	}

	return `${query}${paginationQuery.replace('?', '&')}`;
};

const extractAgreementTypes = (payload: unknown): IAgreementTypeItem[] => {
	const data = payload as Record<string, unknown>;
	const items = Array.isArray(data?.items) ? data.items : [];

	return items.map((item) => {
		const agreementType = item as Record<string, unknown>;
		return {
			id: Number(agreementType?.id ?? 0),
			name: String(agreementType?.name ?? ''),
			description:
				agreementType?.description === null
					? null
					: String(agreementType?.description ?? '')
		};
	});
};

const extractAgreementStatuses = (payload: unknown): IAgreementStatusItem[] => {
	const data = payload as Record<string, unknown>;
	const items = Array.isArray(data?.items) ? data.items : [];

	return items.map((item) => {
		const agreementStatus = item as Record<string, unknown>;
		return {
			id: Number(agreementStatus?.id ?? 0),
			name: String(agreementStatus?.name ?? ''),
			description:
				agreementStatus?.description === null
					? null
					: String(agreementStatus?.description ?? '')
		};
	});
};

async function fetchAgreementTypes(forceRefresh = false): Promise<IAgreementTypeItem[]> {
	if (!forceRefresh && agreementTypesCache) {
		return agreementTypesCache;
	}

	const res = await get('api/agreement-types');
	const types = extractAgreementTypes(res.data);
	agreementTypesCache = types;
	return types;
}

async function fetchAgreementStatuses(
	forceRefresh = false
): Promise<IAgreementStatusItem[]> {
	if (!forceRefresh && agreementStatusesCache) {
		return agreementStatusesCache;
	}

	const res = await get('api/agreement-statuses');
	const statuses = extractAgreementStatuses(res.data);
	agreementStatusesCache = statuses;
	return statuses;
}

// Rezolvă tipul aplicație → item de nomenclator DUPĂ id (id-ul stabil din config), NU
// după nume. Enrichment-ul (name/description) vine din api/agreement-types când e
// disponibil; dacă fetch-ul eșuează sau id-ul lipsește din listă, întoarce un item
// sintetic cu id-ul fix — astfel get/create/update rămân corecte indiferent de mediul
// de deploy (numele poate fi RO sau EN).
async function resolveAgreementTypeByAppType(
	type: AgrementsType
): Promise<IAgreementTypeItem> {
	const typeId = AGREEMENT_TYPE_CONFIG[type].id;
	try {
		const types = await fetchAgreementTypes();
		const matched = types.find((item) => item.id === typeId);
		if (matched) return matched;
	} catch {
		// endpoint indisponibil (ex. pagini publice fără token) — cădem pe id-ul static
	}
	return { id: typeId, name: AGREEMENT_TYPE_CONFIG[type].label, description: null };
}

const resolveAgreementStatusKeyByName = (name: string): AgrementsStatus =>
	AGREEMENT_STATUS_NAME_MAP[name] ?? 'draft';

async function resolveAgreementStatusIdByKey(
	status: AgrementsStatus
): Promise<number | null> {
	const statuses = await fetchAgreementStatuses();
	const matchedStatus = statuses.find(
		(item) => resolveAgreementStatusKeyByName(item.name) === status
	);

	return matchedStatus?.id ?? null;
}

// id → tip aplicație, direct din map-ul static (independent de nume/cache).
const getAgreementTypeById = (typeId: number): AgrementsType =>
	AGREEMENT_TYPE_BY_ID[typeId] ?? 'GDPR';

const normalizeAgreementType = (payload: unknown): { type: AgrementsType; typeId: number } => {
	if (payload && typeof payload === 'object') {
		const data = payload as { id?: number; name?: string };
		if (data.id) {
			return {
				type: getAgreementTypeById(data.id),
				typeId: data.id
			};
		}

		if (data.name && AGREEMENT_TYPE_NAME_MAP[data.name]) {
			const type = AGREEMENT_TYPE_NAME_MAP[data.name];
			return {
				type,
				typeId: AGREEMENT_TYPE_CONFIG[type].id
			};
		}
	}

	if (typeof payload === 'string' && AGREEMENT_TYPE_NAME_MAP[payload]) {
		const type = AGREEMENT_TYPE_NAME_MAP[payload];
		return {
			type,
			typeId: AGREEMENT_TYPE_CONFIG[type].id
		};
	}

	if (typeof payload === 'number') {
		return {
			type: getAgreementTypeById(payload),
			typeId: payload
		};
	}

	return {
		type: 'GDPR',
		typeId: AGREEMENT_TYPE_CONFIG.GDPR.id
	};
};

const normalizeAgreementStatus = (
	payload: unknown
): { status: AgrementsStatus; statusId?: number } => {
	if (payload && typeof payload === 'object') {
		const data = payload as { id?: number; name?: string };
		if (data.name && AGREEMENT_STATUS_NAME_MAP[data.name]) {
			return {
				status: AGREEMENT_STATUS_NAME_MAP[data.name],
				statusId: data.id
			};
		}
	}

	if (typeof payload === 'string' && AGREEMENT_STATUS_NAME_MAP[payload]) {
		return {
			status: AGREEMENT_STATUS_NAME_MAP[payload]
		};
	}

	return {
		status: 'draft'
	};
};

const normalizeAgreement = (payload: unknown): IAgrements => {
	const data = payload as Record<string, unknown>;
	const normalizedType = normalizeAgreementType(data?.type ?? data?.typeName ?? data?.typeId);
	const normalizedStatus = normalizeAgreementStatus(
		data?.status ?? data?.statusName ?? data?.statusId
	);

	return {
		id: Number(data?.id ?? 0),
		version: String(data?.version ?? ''),
		type: normalizedType.type,
		typeId: Number(data?.typeId ?? normalizedType.typeId),
		description: String(data?.description ?? ''),
		status: normalizedStatus.status,
		statusId:
			Number(data?.statusId ?? normalizedStatus.statusId ?? 0) || normalizedStatus.statusId,
		createdAt: String(data?.createdAt ?? '')
	};
};

const extractAgreements = (payload: unknown): IAgrements[] => {
	const data = payload as Record<string, unknown>;
	const items = Array.isArray(data?.items)
		? data.items
		: Array.isArray(data?.agrements)
			? data.agrements
			: [];

	return items.map(normalizeAgreement);
};

async function fetchAgrements(
	type: AgrementsType,
	page: number,
	items: number,
	onResponse?: (page: number, items: number) => void
): Promise<IAgrements[]> {
	try {
		// Filtrarea listei se face pe id-ul stabil al tipului (nu pe nume).
		const query = generateQueryString({
			TypeId: AGREEMENT_TYPE_CONFIG[type].id,
			PageNumber: page + 1,
			PageSize: items
		});
		const res = await get(`api/agreements${query}`);
		onResponse && onResponse(res.data?.totalPages ?? res.data?.pages, res?.data?.totalCount);
		return extractAgreements(res.data);
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('clauses.errorLoad'));
	}
}

async function fetchAgrementsQuery(
	query: string,
	page: number,
	items: number,
	onResponse?: (page: number, items: number) => void,
	type?: AgrementsType
): Promise<IAgrements[]> {
	try {
		// TypeId din id-ul stabil al tipului (nu din nume); dacă query-ul îl are deja, îl păstrăm.
		const typeId = type ? AGREEMENT_TYPE_CONFIG[type].id : null;
		const normalizedQuery = query.includes('TypeId=')
			? query
			: generateQueryString({
				TypeId: typeId,
				...Object.fromEntries(new URLSearchParams(query.replace(/^\?/, '')).entries())
			});
		const res = await get(
			`api/agreements${appendPaginationParams(normalizedQuery, page, items)}`
		);
		onResponse && onResponse(res.data?.totalPages ?? res.data?.pages, res?.data?.totalCount);
		return extractAgreements(res.data);
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('clauses.errorLoad'));
	}
}

async function fetchSingleAgrement(id: number | string): Promise<IAgrements> {
	try {
		await Promise.all([fetchAgreementTypes(), fetchAgreementStatuses()]);
		const res = await get(`api/agreements/${id}`);
		return normalizeAgreement(res.data?.agrement ?? res.data);
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('clauses.errorLoadOne'));
	}
}

async function postAgrement(
	obj: ICreateAgrements
): Promise<{ status: number; id?: number }> {
	try {
		const res = await post('api/agreements', { ...obj });
		if (res?.status === 201) {
			const created = (res?.data?.data ?? res?.data) as { id?: number } | undefined;
			return { status: 201, id: created?.id };
		}
		return { status: 400 };
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('clauses.errorCreate'));
	}
}

async function publishAgrement(id: number): Promise<string | number> {
	try {
		const res = await post(`api/agreements/${id}/publish`);
		return res?.status === 200 || res?.status === 201 ? 200 : 400;
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('clauses.errorPublish'));
	}
}

async function deleteAgrement(id: number): Promise<string | number> {
	try {
		const res = await del(`api/agreements/${id}`);
		return res?.status === 200 || res?.status === 204 ? 200 : 400;
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('clauses.errorDelete'));
	}
}

export {
	fetchAgrements,
	postAgrement,
	resolveAgreementTypeByAppType,
	resolveAgreementStatusIdByKey,
	fetchSingleAgrement,
	fetchAgrementsQuery,
	publishAgrement,
	deleteAgrement
};
